/**
 * "Keeping your catalog fresh" — Guides section (B27.2, 2026-06-12).
 *
 * Every claim verified in-tree: re-running `init` is safe by design
 * (deterministic extraction; dashboard overrides survive a rescan —
 * proven by `override-survives-rescan.integration.test.ts`; the
 * `.syncanixignore` file applies on every run), CI auth is the
 * `SYNCANIX_API_KEY` env var, the sdk-node drift detector is the
 * diagnostics-channel new-endpoint subscriber + `uploadDriftEvents`,
 * and monorepo/polyglot extraction is the merged multi-stack run the
 * messy-monorepo fixtures exercise.
 */
import type { DocPageContent } from '../types';

export const GUIDES_CATALOG_FRESHNESS_PAGE: DocPageContent = {
  slug: 'guides/catalog-freshness',
  titleKey: 'docs.guides.freshness.title',
  descriptionKey: 'docs.guides.freshness.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.guides.freshness.lead' },

    { kind: 'heading', level: 2, id: 'rerun', textKey: 'docs.guides.freshness.rerun.heading' },
    { kind: 'paragraph', textKey: 'docs.guides.freshness.rerun.body' },
    { kind: 'code', language: 'bash', code: 'npx syncanix init' },
    { kind: 'paragraph', textKey: 'docs.guides.freshness.rerun.overrides' },

    { kind: 'heading', level: 2, id: 'ci', textKey: 'docs.guides.freshness.ci.heading' },
    { kind: 'paragraph', textKey: 'docs.guides.freshness.ci.body' },
    {
      kind: 'code',
      language: 'yaml',
      code: [
        '# .github/workflows/syncanix-catalog.yml',
        'name: Refresh Syncanix catalog',
        'on:',
        '  push:',
        '    branches: [main]',
        'jobs:',
        '  refresh:',
        '    runs-on: ubuntu-latest',
        '    steps:',
        '      - uses: actions/checkout@v4',
        '      - uses: actions/setup-node@v4',
        "        with: { node-version: '20' }",
        '      - run: npx syncanix init --yes',
        '        env:',
        '          SYNCANIX_API_KEY: ${{ secrets.SYNCANIX_API_KEY }}',
      ].join('\n'),
    },
    { kind: 'paragraph', textKey: 'docs.guides.freshness.ci.notes' },

    { kind: 'heading', level: 2, id: 'drift', textKey: 'docs.guides.freshness.drift.heading' },
    { kind: 'paragraph', textKey: 'docs.guides.freshness.drift.body' },
    { kind: 'paragraph', textKey: 'docs.guides.freshness.drift.how' },

    {
      kind: 'heading',
      level: 2,
      id: 'monorepo',
      textKey: 'docs.guides.freshness.monorepo.heading',
    },
    { kind: 'paragraph', textKey: 'docs.guides.freshness.monorepo.body' },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.guides.freshness.related.init.title',
          textKey: 'docs.guides.freshness.related.init.text',
          to: { docSlug: 'cli/init' },
        },
        {
          titleKey: 'docs.guides.freshness.related.production.title',
          textKey: 'docs.guides.freshness.related.production.text',
          to: { docSlug: 'guides/go-to-production' },
        },
      ],
    },
  ],
};
